/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { CSSProperties, useMemo } from "react";
import { css, cx } from "@panda/css";

import { OptimizedCosmosAssetFragment } from "../types";
import { CosmosAsset } from "../asset";
import { HYGRAPH_MEDIA_DOMAIN } from "@components/constants";

const styles = css({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	position: "absolute",
	inset: "calc(var(--image-blur-pixels, 125px) * -2)",
	opacity: "var(--image-opacity, cover)",
	pointerEvents: "none",
});

const imageStyles = css({
	w: "full",
	objectFit: "var(--image-object-fit, cover)",
});

type ObjectFit = "fill" | "contain" | "cover" | "none" | "scale-down";

export interface BlurredImageProps {
	className?: string;
	image?: OptimizedCosmosAssetFragment | null;
	blurPixels?: number;
	objectFit?: ObjectFit;
	opacity?: number;
	width: number;
	height: number;
	disableAutoplay?: boolean;
}

export interface BlurredImageCssVariables extends CSSProperties {
	"--image-blur-pixels"?: string;
	"--image-object-fit"?: ObjectFit;
	"--image-opacity"?: string;
}

const updateBlurredImageUrl = (
	image: OptimizedCosmosAssetFragment,
	blurPixels: number,
): OptimizedCosmosAssetFragment => {
	return {
		id: image.id,
		width: image.width,
		height: image.height,
		altText: image.altText,
		attribution: image.attribution,
		url: `https://${HYGRAPH_MEDIA_DOMAIN}/process?blur=${blurPixels}&url=${image.url}`,
	};
};

export const BlurredImage = ({
	className,
	image,
	blurPixels = 125,
	objectFit = "cover",
	opacity = 0.8,
	width,
	height,
	disableAutoplay = true,
}: BlurredImageProps): React.JSX.Element | null => {
	const blurredAsset = useMemo(() => {
		if (!image) return null;
		return updateBlurredImageUrl(image, blurPixels);
	}, [image, blurPixels]);

	if (!blurredAsset) return null;

	const styleVars: BlurredImageCssVariables = {
		"--image-object-fit": objectFit,
		"--image-opacity": `${opacity}`,
		"--image-blur-pixels": `${blurPixels}px`,
		width: "unset",
	};

	return (
		<div className={cx("blurred-image", className, styles)} style={styleVars}>
			<CosmosAsset
				role="presentation"
				alt=""
				className={cx("blurred-image__img", imageStyles)}
				asset={blurredAsset}
				width={width || 1792}
				height={height || 810}
				disableAutoplay={disableAutoplay}
				hideAttribution
			/>
		</div>
	);
};
